import { useTranslation } from "react-i18next";
import { Combobox } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ProjectSummaryDto } from "@/types/project";

interface ActionBarProps {
  projects: ProjectSummaryDto[];
  activeProjectId: number;
  onProjectChange: (id: number) => void;
  canExecute: boolean;
  selectedCount: number;
  onPreview: () => void;
  onSavePdf: () => void;
}

// Shared so Preview/Save PDF badges can never drift out of sync
function SelectedCountBadge({ count }: Readonly<{ count: number }>) {
  if (count <= 0) {
    return null;
  }

  return <Badge variant="secondary" className="rounded-md">{count}</Badge>;
}

export function ActionBar({
  projects,
  activeProjectId,
  onProjectChange,
  canExecute,
  selectedCount,
  onPreview,
  onSavePdf,
}: Readonly<ActionBarProps>) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 border-b px-3 py-2">
      {/* Project — Searchable Combobox */}
      <div
        className="flex items-center gap-1.5"
        title={t("actionBar.tooltips.project")}
      >
        <label className="text-sm font-medium">{t("actionBar.project")}</label>
        <Combobox
          options={projects
            .slice()
            .sort((a, b) => a.projectNumber.localeCompare(b.projectNumber))
            .map((p) => ({
              value: String(p.projectId),
              label: String(p.projectNumber),
            }))}
          value={String(activeProjectId)}
          onValueChange={(v) => onProjectChange(Number(v))}
          placeholder="Select project"
          searchPlaceholder="Search projects..."
          emptyText="No projects found."
          className="w-[200px]"
        />
      </div>

      {/* Execution Buttons */}
      <div className="ml-auto flex gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={onPreview}
                  disabled={!canExecute}
                >
                  {t("actions.preview")} <SelectedCountBadge count={selectedCount} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t("actionBar.tooltips.preview")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={onSavePdf}
                  disabled={!canExecute}
                  variant="outline"
                >
                  {t("actions.savePdf")} <SelectedCountBadge count={selectedCount} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t("actionBar.tooltips.savePdf")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
